import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { useSite } from "@/context/SiteContext";
import { telLink, waLink } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/partner", label: "Become a Partner" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { settings } = useSite();
  const location = useLocation();

  return (
    <header data-testid="main-navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-brand-ivory/85 border-b border-brand-deep/10">
      <div className="container-site flex items-center justify-between h-[72px]">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-brand-deep ${
                  isActive ? "text-brand-deep font-semibold" : "text-brand-charcoal/65"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/partner"
            data-testid="nav-become-distributor-cta"
            className="rounded-full bg-brand-gold px-5 py-2.5 text-sm font-bold text-brand-deep transition-all hover:bg-brand-deep hover:text-white shadow-sm hover:shadow-lg"
          >
            Become a Distributor
          </Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-11 h-11 flex items-center justify-center rounded-xl text-brand-deep"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-brand-ivory border-t border-brand-deep/10"
            aria-label="Mobile"
          >
            <div className="container-site py-5 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 px-3 rounded-xl text-base font-medium ${isActive ? "bg-brand-deep/5 text-brand-deep font-semibold" : "text-brand-charcoal/70"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex gap-3 pt-3">
                <a
                  href={telLink(settings)}
                  data-testid="mobile-nav-call"
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-brand-deep/20 py-3 text-sm font-semibold text-brand-deep"
                >
                  <Phone className="w-4 h-4" /> Call
                </a>
                <a
                  href={waLink(settings, "Hello ICROPSIA team, I would like to know more about your seeds.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-whatsapp"
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-brand-deep py-3 text-sm font-semibold text-white"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
